import { useEffect, useState } from "react";
import ContributorSummaryView from "./ContributorSummaryView";
import { api } from "../../../services/backend/api";
import { Button, message, Space, Typography } from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import { getWeekEndingDate } from "../../../utils/formatters";

const NO_CACHE_MESSAGE = "No data in cache. Click Refresh to load.";

/**
 * Fetches contributor hours summary (current week, last week, lifetime) from the new API
 * and renders ContributorHoursSummaryView.
 * Supports both project and project-objective scope.
 * When loaded, calls onLastSyncedAt(last_synced_at) so parent can show "Last refreshed at" when this (slow) API completes.
 */
const ContributorSummary = ({ projectId, objectiveId }) => {
  const { Text } = Typography;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [summaryRefreshTick, setSummaryRefreshTick] = useState(0);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (!projectId && !objectiveId) return;

    setLoading(true);
    const basePath = objectiveId
      ? `/performance/po/contributor-summary/${objectiveId}`
      : `/performance/p/contributor-summary/${projectId}`;
    const weekEnd = getWeekEndingDate();
    const isRefresh = summaryRefreshTick > 0;
    const qs = `weekEnd=${weekEnd}${isRefresh ? "&refresh=true" : ""}`;

    api
      .get(`${basePath}?${qs}`)
      .then((response) => {
        const list =
          response && response.success && Array.isArray(response.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
        if (!cancelled) {
          setData(list);
          setLastSyncedAt(
            response?.last_synced_at ? new Date(response.last_synced_at) : null,
          );
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Error loading contributor hours summary:", err);
          message.error("Failed to load contributor hours summary");
          setData([]);
          setLastSyncedAt(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, objectiveId, summaryRefreshTick]);

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <Space align="center" wrap>
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={() => setSummaryRefreshTick((t) => t + 1)}
            loading={loading}
          >
            Refresh
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {lastSyncedAt
              ? `Last refreshed at ${lastSyncedAt.toLocaleString()}`
              : NO_CACHE_MESSAGE}
          </Text>
        </Space>
      </div>
      <ContributorSummaryView data={data} loading={loading} />
    </>
  );
};

export default ContributorSummary;
